import { RISK_LEVELS } from '@silverfort-nodelibs/common/utils/consts';
import moment from 'moment';
import React from 'react';
import { Dropdown } from '@silverfort/sf-components';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RISK_RANGE_FILTER } from '@silverfort-nodelibs/common/utils/consts';

const riskLevelLabelMap = {
    [RISK_LEVELS.LOW.value]: 'Low',
    [RISK_LEVELS.MEDIUM.value]: 'Medium',
    [RISK_LEVELS.HIGH.value]: 'High',
    [RISK_LEVELS.CRITICAL.value]: 'Critical'
};

const buildRiskTimeline = (data, currentRiskLevel, rangeFilter) => {
    const range = Object.values(RISK_RANGE_FILTER).find((range) => range.value === rangeFilter);
    const graphStartDate = moment().subtract(range?.subValueInDays, 'days');
    if (!data || data.length === 0) {
        return [
            {
                time: graphStartDate,
                risk_level: currentRiskLevel,
                date: graphStartDate.format('MMM DD, YYYY')
            },
            {
                time: Date.now(),
                risk_level: currentRiskLevel,
                date: moment().format('MMM DD, YYYY')
            }
        ];
    }

    const format = data?.length <= 5 ? 'MMM DD, YYYY' : 'MMM DD';
    const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);

    // Add first element based on range and old risk level
    const timeline: { time: number; risk_level: number; date: string }[] = [
        {
            time: graphStartDate.valueOf(),
            risk_level: sorted[0].old_risk_level,
            date: graphStartDate.format(format)
        }
    ];

    for (const item of sorted) {
        timeline.push({
            time: item.timestamp,
            risk_level: item.risk_level,
            date: moment(item.timestamp).format(format)
        });
    }

    timeline.push({
        time: Date.now(),
        risk_level: currentRiskLevel,
        date: moment().format(format)
    });

    return timeline;
};

const IdentityDataRiskGraph = ({ data, currentRiskLevel, rangeFilter, setRangeFilter }) => {
    const timelineData = buildRiskTimeline(data, currentRiskLevel, rangeFilter);

    return (
        <div className="h-full w-full  font-sf">
            <div className="flex h-12 p-4 items-center border-b border-sf-gray-100 justify-between">
                <div className="text-13 font-medium text-sf-gray-800">Risk Over Time</div>
                <Dropdown
                    className="w-[128px]"
                    size="small"
                    onChange={(value) => setRangeFilter(value as string)}
                    selectedItems={rangeFilter}
                    items={Object.values(RISK_RANGE_FILTER)}
                    variant="trigger"
                />
            </div>
            <div className="p-4">
                <ResponsiveContainer width={'100%'} height={140} className={'text-10 text-sf-gray-500'}>
                    <AreaChart data={timelineData}>
                        <defs>
                            <linearGradient id="colorRisk" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#007B8F" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#007B8F" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickMargin={10} />
                        <YAxis
                            type="number"
                            domain={[1, 4]}
                            ticks={[1, 2, 3, 4]}
                            tickFormatter={(tick) => riskLevelLabelMap[tick]}
                        />
                        <Tooltip
                            labelFormatter={(label) => `Time: ${label}`}
                            formatter={(value: number) => [`${riskLevelLabelMap[value]}`, 'Risk Level']}
                        />
                        <Area
                            type="monotone"
                            dataKey="risk_level"
                            stroke="#007B8F"
                            fillOpacity={1}
                            fill="url(#colorRisk)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default IdentityDataRiskGraph;
